import { ContentChild, Directive, Injector } from "@angular/core";
import { EmphasisMixin, EmphasisMixinInputs } from "./mixins/emphasis";
import { FieldStateMixin, FieldStateMixinInputs } from "./mixins/field-state";
import { DirectiveMixin } from "./mixins/mixin";
import { SizeMixin, SizeMixinInputs } from "src/shared/base-components/mixins/size";
import { InputtedMixin, InputtedMixinInputs } from "./mixins/inputted";
import { FieldIconsMixin, FieldIconsMixinInputs } from "./mixins/field-icons";

import qcompare from "qcompare";
import LoggerService from "@bfx/logger";

const logger = LoggerService.for(__dirname);

const Mixins = FieldIconsMixin(
  FieldStateMixin(SizeMixin(InputtedMixin(EmphasisMixin(DirectiveMixin)))),
);
const MixinInputs = [
  ...FieldIconsMixinInputs,
  ...FieldStateMixinInputs,
  ...SizeMixinInputs,
  ...InputtedMixinInputs,
  ...EmphasisMixinInputs,
];

const BFX_FIELD_DIRECTIVE = "bfx-field";
@Directive({
  selector: `[${BFX_FIELD_DIRECTIVE}]`,
  host: { class: "bfx-field-container" },
  standalone: false,
})
export abstract class BfxFieldDirective extends Mixins {
  @ContentChild("text") textElement: HTMLInputElement;

  abstract directiveInputName: string;

  constructor(public injector: Injector) {
    super(injector);
  }

  get classes(): Record<string, boolean> {
    let previousValue = this._previousValuesMap.get("classes");

    let currentValue = {
      "bfx-field": true,
      "bfx-field-enabled-bold": "enabled" === this.fieldState && this.bold,
      "bfx-field-read-only-bold": "read-only" === this.fieldState && this.bold,
      "bfx-field-hover-bold": "hover" === this.fieldState && this.bold,
      "bfx-field-invalid-bold": "invalid" === this.fieldState && this.bold,
      "bfx-field-active-bold": "active" === this.fieldState && this.bold,
      "bfx-field-loading-bold": "loading" === this.fieldState && this.bold,
      "bfx-field-disabled-bold": "disabled" === this.fieldState && this.bold,
      "bfx-field-enabled-subtle": "enabled" === this.fieldState && this.subtle,
      "bfx-field-read-only-subtle": "read-only" === this.fieldState && this.subtle,
      "bfx-field-hover-subtle": "hover" === this.fieldState && this.subtle,
      "bfx-field-active-subtle": "active" === this.fieldState && this.subtle,
      "bfx-field-invalid-subtle": "invalid" === this.fieldState && this.subtle,
      "bfx-field-loading-subtle": "loading" === this.fieldState && this.subtle,
      "bfx-field-disabled-subtle": "disabled" === this.fieldState && this.subtle,
      "bfx-field-sm-bold": this.sm && this.bold,
      "bfx-field-md-bold": this.md && this.bold,
      "bfx-field-lg-bold": this.lg && this.bold,
      "bfx-field-sm-subtle": this.sm && this.subtle,
      "bfx-field-md-subtle": this.md && this.subtle,
      "bfx-field-lg-subtle": this.lg && this.subtle,
    };

    if (!qcompare(previousValue, currentValue)) {
      this._previousValuesMap.set("classes", currentValue);
      return currentValue;
    }

    return previousValue;
  }

  get inputClasses(): Record<string, boolean> {
    let previousValue = this._previousValuesMap.get("inputClasses");

    let currentValue = {
      [`${this.directiveInputName}`]: true,
      "bfx-field-placeholder-sm-bold": this.sm && this.bold && this.inputted === false,
      "bfx-field-placeholder-md-bold": this.md && this.bold && this.inputted === false,
      "bfx-field-placeholder-lg-bold": this.lg && this.bold && this.inputted === false,
      "bfx-field-inputted-sm-bold": this.sm && this.bold && this.inputted,
      "bfx-field-inputted-md-bold": this.md && this.bold && this.inputted,
      "bfx-field-inputted-lg-bold": this.lg && this.bold && this.inputted,
      "bfx-field-placeholder-sm-subtle": this.sm && this.subtle && this.inputted === false,
      "bfx-field-placeholder-md-subtle": this.md && this.subtle && this.inputted === false,
      "bfx-field-placeholder-lg-subtle": this.lg && this.subtle && this.inputted === false,
      "bfx-field-inputted-sm-subtle": this.sm && this.subtle && this.inputted,
      "bfx-field-inputted-md-subtle": this.md && this.subtle && this.inputted,
      "bfx-field-inputted-lg-subtle": this.lg && this.subtle && this.inputted,
      "bfx-field-placeholder-enabled-bold":
        "enabled" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-placeholder-hover-bold":
        "hover" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-placeholder-active-bold":
        "active" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-placeholder-read-only-bold":
        "read-only" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-placeholder-loading-bold":
        "loading" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-placeholder-disabled-bold":
        "disabled" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-inputted-enabled-bold":
        "enabled" === this.fieldState && this.bold && this.inputted,
      "bfx-field-inputted-hover-bold": "hover" === this.fieldState && this.bold && this.inputted,
      "bfx-field-inputted-active-bold": "active" === this.fieldState && this.bold && this.inputted,
      "bfx-field-inputted-read-only-bold":
        "read-only" === this.fieldState && this.bold && this.inputted,
      "bfx-field-inputted-loading-bold":
        "loading" === this.fieldState && this.bold && this.inputted,
      "bfx-field-inputted-disabled-bold":
        "disabled" === this.fieldState && this.bold && this.inputted,
      "bfx-field-placeholder-enabled-subtle":
        "enabled" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-placeholder-hover-subtle":
        "hover" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-placeholder-active-subtle":
        "active" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-placeholder-read-only-subtle":
        "read-only" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-placeholder-loading-subtle":
        "loading" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-placeholder-disabled-subtle":
        "disabled" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-inputted-enabled-subtle":
        "enabled" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-inputted-hover-subtle":
        "hover" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-inputted-active-subtle":
        "active" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-inputted-read-only-subtle":
        "read-only" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-inputted-loading-subtle":
        "loading" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-inputted-disabled-subtle":
        "disabled" === this.fieldState && this.subtle && this.inputted,
    };

    if (!qcompare(previousValue, currentValue)) {
      this._previousValuesMap.set("inputClasses", currentValue);
      return currentValue;
    }

    return previousValue;
  }

  directiveHost(host: Record<string, any>) {
    let previousValue = this._previousValuesMap.get("directiveHost");
    let currentValue = MixinInputs.reduce((value, key) => {
      value[key] = host[key];
      return value;
    }, {});

    if (!qcompare(previousValue, currentValue)) {
      this._previousValuesMap.set("directiveHost", currentValue);
      this.applyDirectiveInput(currentValue);
      return currentValue;
    }

    return previousValue;
  }

  iconClasses(): Record<string, boolean> {
    return {
      "bfx-field-icon-inputted-active-bold":
        "active" === this.fieldState && this.bold && this.inputted,
      "bfx-field-icon-inputted-active-subtle":
        "active" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-icon-inputted-disabled-bold":
        "disabled" === this.fieldState && this.bold && this.inputted,
      "bfx-field-icon-inputted-disabled-subtle":
        "disabled" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-icon-inputted-enabled-bold":
        "enabled" === this.fieldState && this.bold && this.inputted,
      "bfx-field-icon-inputted-enabled-subtle":
        "enabled" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-icon-inputted-hover-bold":
        "hover" === this.fieldState && this.bold && this.inputted,
      "bfx-field-icon-inputted-hover-subtle":
        "hover" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-icon-inputted-invalid-bold":
        "invalid" === this.fieldState && this.bold && this.inputted,
      "bfx-field-icon-inputted-invalid-subtle":
        "invalid" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-icon-inputted-lg-bold": this.lg && this.bold && this.inputted,
      "bfx-field-icon-inputted-lg-subtle": this.lg && this.subtle && this.inputted,
      "bfx-field-icon-inputted-loading-bold":
        "loading" === this.fieldState && this.bold && this.inputted,
      "bfx-field-icon-inputted-loading-subtle":
        "loading" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-icon-inputted-md-bold": this.md && this.bold && this.inputted,
      "bfx-field-icon-inputted-md-subtle": this.md && this.subtle && this.inputted,
      "bfx-field-icon-inputted-read-only-bold":
        "read-only" === this.fieldState && this.bold && this.inputted,
      "bfx-field-icon-inputted-read-only-subtle":
        "read-only" === this.fieldState && this.subtle && this.inputted,
      "bfx-field-icon-inputted-sm-bold": this.sm && this.bold && this.inputted,
      "bfx-field-icon-inputted-sm-subtle": this.sm && this.subtle && this.inputted,
      "bfx-field-icon-placeholder-active-bold":
        "active" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-active-subtle":
        "active" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-disabled-bold":
        "disabled" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-disabled-subtle":
        "disabled" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-enabled-bold":
        "enabled" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-enabled-subtle":
        "enabled" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-hover-bold":
        "hover" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-hover-subtle":
        "hover" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-invalid-bold":
        "invalid" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-invalid-subtle":
        "invalid" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-lg-bold": this.lg && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-lg-subtle": this.lg && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-loading-bold":
        "loading" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-loading-subtle":
        "loading" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-md-bold": this.md && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-md-subtle": this.md && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-read-only-bold":
        "read-only" === this.fieldState && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-read-only-subtle":
        "read-only" === this.fieldState && this.subtle && this.inputted === false,
      "bfx-field-icon-placeholder-sm-bold": this.sm && this.bold && this.inputted === false,
      "bfx-field-icon-placeholder-sm-subtle": this.sm && this.subtle && this.inputted === false,
    };
  }
}
